import React, { useEffect, useState } from "react";
import type { ShopHeaderDesign } from "../../../../../../domain/types/company-shop";
import type { CategoryDto } from "../../../../../../domain/types/company/company-product-system";
import { companyProductSystemApiService } from "../../../../../../infrastructure/api/company/company-product-system-api-service";
import { useAuthStore } from "../../../../../stores/auth-store";
import { HEADER_PRESETS, HEADER_PRESET_LABELS, type HeaderPresetKey } from "../../constants";
import { applyHeaderPreset, patchHeaderDesign } from "../../utils";
import { Field, Segmented, SettingsCard, ToggleRow } from "../ui/SettingsUI";
import { ThemeFields } from "./DesignShared";
import { HeaderMenuEditor } from "./HeaderMenuEditor";

interface HeaderSettingsProps {
  headerDesign: ShopHeaderDesign;
  setHeaderDesign: React.Dispatch<React.SetStateAction<ShopHeaderDesign>>;
}

const PRESET_OPTIONS = [
  ...(Object.keys(HEADER_PRESETS) as HeaderPresetKey[]).map((key) => ({ value: key as string, label: HEADER_PRESET_LABELS[key] })),
];

/** 헤더 탭: 테마(프리셋 · 색 · 로고 · 고정) + 메뉴(순서 · 문구 · 노출 · 링크) */
export const HeaderSettings = ({ headerDesign, setHeaderDesign }: HeaderSettingsProps) => {
  const { user } = useAuthStore();
  const [categories, setCategories] = useState<CategoryDto[]>([]);

  // 메뉴 링크로 고를 카테고리 · 상품. 못 불러와도 '직접 입력'으로 링크를 걸 수 있다
  useEffect(() => {
    const companyId = user?.companyId;
    if (!companyId) return;
    let cancelled = false;
    companyProductSystemApiService
      .getCategories(companyId)
      .then((response) => {
        if (!cancelled) setCategories(response.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.companyId]);

  const patch = (values: Partial<ShopHeaderDesign>) => setHeaderDesign((original) => patchHeaderDesign(original, values));

  return (
    <>
      <SettingsCard title="테마" description="쇼핑몰 상단 영역의 색과 로고 위치입니다.">
        <ThemeFields
          presetKey={headerDesign.presetKey}
          presetOptions={headerDesign.presetKey === "CUSTOM" ? [...PRESET_OPTIONS, { value: "CUSTOM", label: "직접 설정" }] : PRESET_OPTIONS}
          onPreset={(presetKey) => {
            if (presetKey !== "CUSTOM") setHeaderDesign((original) => applyHeaderPreset(original, presetKey as HeaderPresetKey));
          }}
          backgroundColor={headerDesign.backgroundColor}
          textColor={headerDesign.textColor}
          onColor={patch}
        />
        <Field label="로고 위치">
          <Segmented
            ariaLabel="로고 위치"
            value={headerDesign.logoAlignment}
            options={[
              { value: "LEFT", label: "왼쪽" },
              { value: "CENTER", label: "가운데" },
            ]}
            onChange={(logoAlignment) => patch({ logoAlignment })}
          />
        </Field>
        <ToggleRow
          label="스크롤해도 헤더 고정"
          description="화면을 내려도 헤더가 위에 남습니다."
          checked={headerDesign.stickyEnabled}
          onChange={(stickyEnabled) => patch({ stickyEnabled })}
        />
      </SettingsCard>

      <SettingsCard
        title="메뉴"
        aside={`${headerDesign.menuItems.filter((item) => item.visible).length}/${headerDesign.menuItems.length}개 노출`}
        description="끌어서 순서를 바꾸고 스위치로 노출합니다. 기본 메뉴는 이름을 바꿀 수 없습니다. 카테고리 · 상품 · 직접 링크로 메뉴를 추가할 수 있습니다."
      >
        <HeaderMenuEditor items={headerDesign.menuItems} categories={categories} onChange={(menuItems) => patch({ menuItems })} />
      </SettingsCard>
    </>
  );
};
